import { useEffect, useState, useCallback, useMemo } from "react"
import { useAuthState } from "@/hooks/useAuth"
import { useUserData } from "@/hooks/useUserData"
import { QUERY_TATTOOS_BY_COLLECTION_SIMILARITY_ENDPOINT } from "@/config/endpoints"
import { db } from "@/lib/firebase"
import { doc, getDoc } from "firebase/firestore"
import type { PortfolioItem } from "@/types/models"
import type { SortingOption } from "@/components/shared/tattoo/filters/SortingFilter"

export interface QueryTattoosByCollectionSimilarityFilters {
  collectionId: string
  collections: ("flashes" | "portfolioItems" | "healedPortfolioItems")[]
  sorting?: SortingOption
  cities?: string[]
  artistId?: string
  style?: string
  includeBooked?: boolean
  deals?: boolean
}

interface TattooItemRef {
  type: "flash" | "portfolioItem" | "healedPortfolioItem"
  id: string
}

interface QueryTattoosResponse {
  left: TattooItemRef[]
  right: TattooItemRef[]
}

const ITEMS_PER_LOAD = 40

/**
 * Hook to query tattoos similar to a collection from the backend
 * Handles fetching document IDs from backend and loading actual documents in batches
 */
export function useQueryTattoosByCollectionSimilarity(filters: QueryTattoosByCollectionSimilarityFilters | undefined) {
  const { user } = useAuthState()
  const { userData, isAdminModeActive } = useUserData()
  const [allLeftItems, setAllLeftItems] = useState<TattooItemRef[]>([])
  const [allRightItems, setAllRightItems] = useState<TattooItemRef[]>([])
  const [loadedItems, setLoadedItems] = useState<PortfolioItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [currentLoadedCount, setCurrentLoadedCount] = useState(0)
  const [activeRequestId, setActiveRequestId] = useState<number>(0)
  
  // Track the current request ID to ignore stale results
  const currentRequestIdRef = useState(() => ({ current: 0 }))[0]

  // Memoize filters to prevent unnecessary re-fetches
  const memoizedFilters = useMemo(() => {
    return JSON.stringify(filters)
  }, [filters])

  // Fetch IDs from backend when filters change
  useEffect(() => {
    if (!filters) {
      // Keep loading state while waiting for filters
      setLoading(true)
      setInitialLoadComplete(false)
      return
    }

    const fetchIds = async () => {
      // Increment request ID for this new query
      currentRequestIdRef.current += 1
      const thisRequestId = currentRequestIdRef.current
      
      try {
        setLoading(true)
        setError(null)
        setLoadedItems([])
        setCurrentLoadedCount(0)
        setInitialLoadComplete(false)

        const requestBody = {
          ...filters,
          userId: user?.uid || null,
          isAdmin: isAdminModeActive,
        }

        console.log("[useQueryTattoosByCollectionSimilarity] Fetching IDs from backend with filters:", filters, "requestId:", thisRequestId)
        console.log("[useQueryTattoosByCollectionSimilarity] Full request body:", requestBody)

        const response = await fetch(QUERY_TATTOOS_BY_COLLECTION_SIMILARITY_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        })

        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}: ${response.statusText}`)
        }

        const data: QueryTattoosResponse = await response.json()
        
        // Check if this request is still current
        if (thisRequestId !== currentRequestIdRef.current) {
          console.log("[useQueryTattoosByCollectionSimilarity] Ignoring stale response for requestId:", thisRequestId, "current:", currentRequestIdRef.current)
          return
        }
        
        console.log("[useQueryTattoosByCollectionSimilarity] Received items from backend:", {
          leftCount: data.left.length,
          rightCount: data.right.length,
          totalCount: data.left.length + data.right.length,
          requestId: thisRequestId,
        })

        setAllLeftItems(data.left)
        setAllRightItems(data.right)
        setActiveRequestId(thisRequestId)

        // Load first batch of items
        await loadItemsBatch(data.left, data.right, 0, ITEMS_PER_LOAD, thisRequestId)
        
        // Check again before setting complete (in case filters changed during batch load)
        if (thisRequestId === currentRequestIdRef.current) {
          setInitialLoadComplete(true)
        }
      } catch (err) {
        console.error("[useQueryTattoosByCollectionSimilarity] Error fetching IDs:", err)
        // Only set error if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setError(err)
        }
      } finally {
        // Only clear loading if this is still the current request
        if (thisRequestId === currentRequestIdRef.current) {
          setLoading(false)
        }
      }
    }

    fetchIds()
  }, [memoizedFilters, user, userData])

  // Load a batch of items from Firestore
  const loadItemsBatch = async (
    leftItems: TattooItemRef[],
    rightItems: TattooItemRef[],
    startIndex: number,
    count: number,
    requestId: number
  ) => {
    // Calculate how many items to load from each column
    const itemsPerColumn = Math.ceil(count / 2)
    const leftBatch = leftItems.slice(startIndex, startIndex + itemsPerColumn)
    const rightBatch = rightItems.slice(startIndex, startIndex + itemsPerColumn)
    const allItemsToFetch = [...leftBatch, ...rightBatch]

    console.log("[useQueryTattoosByCollectionSimilarity] Loading batch:", {
      startIndex,
      count,
      leftBatchSize: leftBatch.length,
      rightBatchSize: rightBatch.length,
      totalToFetch: allItemsToFetch.length,
      requestId,
    })

    if (allItemsToFetch.length === 0) return

    // Fetch all documents in parallel - now we know exactly which collection to use
    const itemPromises = allItemsToFetch.map(async (itemRef) => {
      try {
        // Map the type to the correct collection name
        let collectionName = 
          itemRef.type === "flash" ? "flashes" : 
          itemRef.type === "healedPortfolioItem" ? "healedPortfolioItems" : 
          "portfolioItems"
        let docRef = doc(db, collectionName, itemRef.id)
        let docSnap = await getDoc(docRef)
        
        // For flashes, also check archived if not found
        if (!docSnap.exists() && itemRef.type === "flash") {
          collectionName = "flashesArchived"
          docRef = doc(db, collectionName, itemRef.id)
          docSnap = await getDoc(docRef)
        }
        
        if (!docSnap.exists()) {
          console.warn(`[useQueryTattoosByCollectionSimilarity] Document ${itemRef.id} not found in ${collectionName}`)
          return null
        }

        const data = docSnap.data()

        if (itemRef.type === "flash" && data.status === "artistDeleted") {
          return null
        }

        const item: PortfolioItem = {
          id: docSnap.id,
          artistId: data.artistId as string,
          type: (data.type as PortfolioItem["type"]) ?? "image",
          storagePath: (data.storagePath as string) ?? "",
          imageUrl: data.imageUrl as string,
          width: (data.width as number) ?? 0,
          height: (data.height as number) ?? 0,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
        }
        
        // Add optional properties
        if (data.compressedImageUrl) item.compressedImageUrl = data.compressedImageUrl
        if (data.styles) item.styleTags = data.styles as string[]
        if (data.caption) item.caption = data.caption as string
        if (data.timedSaves) {
          item.timedSaves = data.timedSaves.map((saveData: any) => ({
            itemId: saveData.itemId,
            userId: saveData.userId,
            savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
          }))
        }

        // Flash-specific properties
        if (itemRef.type === "flash") {
          item.isFlash = true
          if (data.minPrice) item.minPrice = data.minPrice
          if (data.minSize) item.minSize = data.minSize
          if (data.available !== undefined) item.available = data.available
          if (data.booked !== undefined) item.booked = data.booked
          if (data.accessCode) item.accessCode = data.accessCode
          
          // Handle discount field
          if (data.discount) {
            item.discount = {
              absoluteAmount: data.discount.absoluteAmount,
              procentualAccount: data.discount.procentualAccount,
              newPrice: data.discount.newPrice,
              oldPrice: data.discount.oldPrice,
              discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
              discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
            }
          }
        }
        
        // Healed portfolio-specific properties
        if (itemRef.type === "healedPortfolioItem") {
          item.isHealed = true
        }
        
        return item
      } catch (err) {
        console.error(`[useQueryTattoosByCollectionSimilarity] Error fetching document ${itemRef.id}:`, err)
        return null
      }
    })

    const fetchedItems = await Promise.all(itemPromises)
    const validItems = fetchedItems.filter((item): item is PortfolioItem => item !== null)

    // Check if this request is still current before updating state
    if (requestId !== currentRequestIdRef.current) {
      console.log("[useQueryTattoosByCollectionSimilarity] Ignoring stale batch results for requestId:", requestId, "current:", currentRequestIdRef.current)
      return
    }

    console.log("[useQueryTattoosByCollectionSimilarity] Batch loaded:", {
      requested: allItemsToFetch.length,
      found: validItems.length,
      missing: allItemsToFetch.length - validItems.length,
      requestId,
    })

    // Create a map to preserve the order from backend (left/right columns)
    const itemMap = new Map<string, PortfolioItem>()
    validItems.forEach(item => itemMap.set(item.id, item))

    // Reconstruct items in the correct order (interleaving left and right)
    const orderedItems: PortfolioItem[] = []
    const maxLength = Math.max(leftBatch.length, rightBatch.length)
    
    for (let i = 0; i < maxLength; i++) {
      if (i < leftBatch.length && itemMap.has(leftBatch[i].id)) {
        orderedItems.push(itemMap.get(leftBatch[i].id)!)
      }
      if (i < rightBatch.length && itemMap.has(rightBatch[i].id)) {
        orderedItems.push(itemMap.get(rightBatch[i].id)!)
      }
    }

    setLoadedItems(prev => [...prev, ...orderedItems])
    setCurrentLoadedCount(prev => prev + orderedItems.length)
  }

  // Load more items (for infinite scroll)
  const loadMoreItems = useCallback(async () => {
    if (loadingMore) return

    const totalAvailableItems = allLeftItems.length + allRightItems.length
    const hasMore = currentLoadedCount < totalAvailableItems

    if (!hasMore) {
      console.log("[useQueryTattoosByCollectionSimilarity] No more items to load")
      return
    }

    console.log("[useQueryTattoosByCollectionSimilarity] Loading more items...", {
      currentLoaded: currentLoadedCount,
      totalAvailable: totalAvailableItems,
      requestId: activeRequestId,
    })

    setLoadingMore(true)

    try {
      // Calculate which items to load next from each column
      const itemsPerColumn = Math.ceil(currentLoadedCount / 2)
      await loadItemsBatch(allLeftItems, allRightItems, itemsPerColumn, ITEMS_PER_LOAD, activeRequestId)
    } catch (err) {
      console.error("[useQueryTattoosByCollectionSimilarity] Error loading more items:", err)
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, allLeftItems, allRightItems, currentLoadedCount, activeRequestId, filters])

  const totalAvailableItems = allLeftItems.length + allRightItems.length
  const hasMore = currentLoadedCount < totalAvailableItems

  return {
    items: loadedItems,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreItems,
    initialLoadComplete,
    searchScores: undefined,
  }
}
